import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IpsecTunnelerServiceV2, NETWORK_SERVICE, NetworkServiceV2 } from '@netfoundry-ui/shared/apiv2';
import { IpsecTunnelerV2, NetworkV2 } from '@netfoundry-ui/shared/model';
import { Inject } from '@angular/core';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import {
    BridgeHealthDto,
    CompositeHealth,
    IpsecTunnelerEventDto,
    IpsecTunnelerMetricsDto,
    IpsecTunnelerReportingService,
    MetricsWindow,
} from '@netfoundry-ui/shared/services';
import { ChartMarker, RxTxSample } from '@netfoundry-ui/ui/rx-tx-timeline-chart';
import { FlowLink, FlowNode, IPSEC_TUNNELER_FLOW_LINKS } from '@netfoundry-ui/ui/flow-diagram';
import { ZITI_DATA_SERVICE, ZitiDataService } from 'ziti-console-lib';
import { fromEvent, interval, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

type Tab = 'overview' | 'ipsec' | 'metrics';
type EventsWindow = '1h' | '24h' | '7d';
const BRIDGE_HEALTH_POLL_MS = 30_000;
const EVENT_KINDS = ['charon', 'ziti-router'] as const;
const EVENTS_PAGE_SIZE = 20;
const QUICK_WINDOW_SPAN_MS: Record<EventsWindow, number> = {
    '1h': 60 * 60_000,
    '24h': 24 * 60 * 60_000,
    '7d': 7 * 24 * 60 * 60_000,
};

// Keys mirror the CompositeHealth wire-contract enum literals.
/* eslint-disable @typescript-eslint/naming-convention */
const COMPOSITE_LABEL: Record<CompositeHealth, string> = {
    HEALTHY: 'Healthy',
    DEGRADED: 'Degraded',
    DOWN: 'Down',
    STALE: 'Stale',
    PROVISIONING: 'Provisioning',
};
/* eslint-enable @typescript-eslint/naming-convention */

@Component({
    selector: 'netfoundry-ui-ipsec-tunneler-extension',
    templateUrl: './ipsec-tunneler-extension.component.html',
    styleUrls: ['./ipsec-tunneler-extension.component.scss'],
    standalone: false,
})
export class IpsecTunnelerExtensionComponent implements OnInit, OnDestroy {
    model: IpsecTunnelerV2 | null = null;
    isLoading = true;

    // Required for every reporting call alongside networkId. Resolved client-side
    // via a one-shot NetworkServiceV2.getNetwork on tunneler load because the
    // V3 entity doesn't surface it directly.
    networkGroupId: string | null = null;

    activeTab: Tab = 'overview';

    // The "How it works" diagram, with the live values of this tunnel.
    overviewNodes: FlowNode[] = [];
    readonly overviewLinks: FlowLink[] = IPSEC_TUNNELER_FLOW_LINKS;

    bridgeHealth: BridgeHealthDto | null = null;
    bridgeHealthLoading = false;
    lastSeenLabel = '';

    metricsWindow: MetricsWindow = '1h';
    metrics: IpsecTunnelerMetricsDto | null = null;
    metricsLoading = false;
    rxTxSamples: RxTxSample[] = [];
    systemSamples: RxTxSample[] = [];
    livenessSamples: RxTxSample[] = [];
    rekeyMarkers: ChartMarker[] = [];

    events: IpsecTunnelerEventDto[] = [];
    eventsLoading = false;
    eventKindFilter: string | null = 'charon';
    readonly eventKinds = EVENT_KINDS;
    eventsWindow: EventsWindow = '24h';
    eventsPageIndex = 0;
    readonly eventsPageSize = EVENTS_PAGE_SIZE;

    // Committed search term, applied by Elasticsearch. Kept separate from the input so typing does
    // not fire a query per keystroke.
    eventSearch: string | null = null;
    eventSearchInput = '';

    eventsRefreshSeconds = 0;
    private eventsRefresh?: Subscription;
    metricsRefreshSeconds = 0;
    private metricsRefresh?: Subscription;

    associatedServices: any[] = [];
    associatedServiceNames: string[] = [];
    associatedServicePolicies: any[] = [];
    associatedServicePolicyNames: string[] = [];
    servicesLoading = false;
    servicePoliciesLoading = false;

    private readonly destroy$ = new Subject<void>();
    private subscription = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private ipsecTunnelerService: IpsecTunnelerServiceV2,
        @Inject(NETWORK_SERVICE) private networkService: NetworkServiceV2,
        @Inject(ZITI_DATA_SERVICE) private zitiDataService: ZitiDataService,
        private reporting: IpsecTunnelerReportingService,
        private growlerService: GrowlerService
    ) {}

    ngOnInit() {
        this.subscription.add(
            this.route.paramMap.subscribe((params) => {
                const id = params.get('id');
                if (id) {
                    this.load(id);
                }
            })
        );
        this.startBridgeHealthPolling();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.subscription.unsubscribe();
    }

    setTab(tab: Tab) {
        this.activeTab = tab;
        if (tab === 'ipsec' && this.events.length === 0) {
            this.loadEvents();
        }
        if (tab === 'metrics') {
            this.loadMetrics();
        }
        // Each panel polls only while its own tab is on screen; polling elsewhere fetches what
        // nothing displays. The chosen intervals are remembered and resume on return.
        if (tab !== 'ipsec') {
            this.eventsRefresh?.unsubscribe();
            this.eventsRefresh = undefined;
        } else if (this.eventsRefreshSeconds > 0 && !this.eventsRefresh) {
            this.setEventsRefresh(this.eventsRefreshSeconds);
        }
        if (tab !== 'metrics') {
            this.metricsRefresh?.unsubscribe();
            this.metricsRefresh = undefined;
        } else if (this.metricsRefreshSeconds > 0 && !this.metricsRefresh) {
            this.setMetricsRefresh(this.metricsRefreshSeconds);
        }
    }

    setMetricsWindow(w: MetricsWindow) {
        this.metricsWindow = w;
        this.loadMetrics();
    }

    headerActionRequested(action: { name: string }) {
        if (action?.name === 'close') {
            this.goBack();
        }
    }

    setEventKindFilter(kind: string | null) {
        this.eventKindFilter = kind;
        this.eventsPageIndex = 0;
        this.loadEvents();
    }

    setEventsWindow(w: EventsWindow) {
        this.eventsWindow = w;
        this.eventsPageIndex = 0;
        this.loadEvents();
    }

    get pagedEvents(): IpsecTunnelerEventDto[] {
        const start = this.eventsPageIndex * this.eventsPageSize;
        return this.events.slice(start, start + this.eventsPageSize);
    }

    get eventsTotalPages(): number {
        return Math.max(1, Math.ceil(this.events.length / this.eventsPageSize));
    }

    nextEventsPage() {
        if (this.eventsPageIndex < this.eventsTotalPages - 1) this.eventsPageIndex++;
    }

    prevEventsPage() {
        if (this.eventsPageIndex > 0) this.eventsPageIndex--;
    }

    goBack() {
        this.router.navigate(['/network/ipsec-tunnelers']);
    }

    get bridgeHealthLabel(): string {
        const c = this.resolveComposite();
        return c ? COMPOSITE_LABEL[c] : 'Unknown';
    }

    get bridgeHealthClass(): string {
        const c = this.resolveComposite();
        return `health-${(c ?? 'unknown').toLowerCase()}`;
    }

    private resolveComposite(): CompositeHealth | null {
        return this.bridgeHealth?.compositeHealth ?? null;
    }

    get bridgeHealthBadges(): { label: string; class: string }[] {
        const c = this.resolveComposite();
        if (!c) return [];
        const klass = c === 'HEALTHY' ? 'online' : c === 'DEGRADED' ? 'warning' : c === 'DOWN' ? 'unreg' : 'unknown'; // STALE, PROVISIONING
        return [{ label: COMPOSITE_LABEL[c], class: klass }];
    }

    get isStale(): boolean {
        return this.bridgeHealth?.compositeHealth === 'STALE';
    }

    get isNeverReported(): boolean {
        return this.bridgeHealth?.compositeHealth === 'PROVISIONING';
    }

    hasChildSAs(): boolean {
        return (this.bridgeHealth?.childSAs?.length ?? 0) > 0;
    }

    formatBytes(n: number | null | undefined): string {
        if (n == null) return '—';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
        if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MiB`;
        return `${(n / 1024 / 1024 / 1024).toFixed(2)} GiB`;
    }

    formatPct(n: number | null | undefined): string {
        if (n == null) return '—';
        return `${n.toFixed(1)}%`;
    }

    formatEpochMs(t: number | null | undefined): string {
        if (t == null) return '—';
        return new Date(t).toLocaleString();
    }

    private load(id: string) {
        this.isLoading = true;
        this.ipsecTunnelerService.getIpsecTunneler(id).then(
            (tunneler) => {
                this.model = tunneler || null;
                this.overviewNodes = this.buildOverviewNodes();
                this.isLoading = false;
                this.resolveNetworkGroupId();
                this.loadAssociatedZitiData();
            },
            (err) => {
                this.growlerService.show(
                    new GrowlerData('error', 'Error', 'Failed to load IPsec Tunneler', err?.error?.errors?.[0] || '')
                );
                this.isLoading = false;
            }
        );
    }

    private buildOverviewNodes(): FlowNode[] {
        if (!this.model) {
            return [];
        }
        const cidrs = this.model.customerPeerCidrs?.length ? this.model.customerPeerCidrs.join(', ') : '—';
        return [
            {
                title: 'Remote Site',
                tone: 'client',
                rows: [
                    { label: 'Endpoint', value: this.model.customerPublicIp || '—' },
                    { label: 'Subnets', value: cidrs },
                ],
            },
            {
                title: 'IPsec Bridge',
                tone: 'managed',
                rows: [{ label: 'Bridge IP', value: this.model.host?.ipAddress || '—' }],
            },
            {
                title: 'This Network',
                tone: 'private',
                rows: [{ label: 'Edge Router', value: `${this.model.name}-router` }],
            },
        ];
    }

    private loadAssociatedZitiData() {
        const identityId = this.model?.zitiEndpointIdentityId;
        if (!identityId) return;

        this.servicesLoading = true;
        this.zitiDataService.getSubdata('identities', identityId, 'services').then(
            (result: any) => {
                this.associatedServices = result?.data ?? [];
                this.associatedServiceNames = this.associatedServices.map((svc: any) => svc.name);
                this.servicesLoading = false;
            },
            () => {
                this.servicesLoading = false;
            }
        );

        this.servicePoliciesLoading = true;
        this.zitiDataService.getSubdata('identities', identityId, 'service-policies').then(
            (result: any) => {
                this.associatedServicePolicies = result?.data ?? [];
                this.associatedServicePolicyNames = this.associatedServicePolicies.map((p: any) => p.name);
                this.servicePoliciesLoading = false;
            },
            () => {
                this.servicePoliciesLoading = false;
            }
        );
    }

    private resolveNetworkGroupId() {
        if (!this.model?.networkId) return;
        this.networkService.getNetwork(this.model.networkId).then(
            (network: NetworkV2 | undefined) => {
                this.networkGroupId = network?.networkGroupId ?? null;
                if (this.networkGroupId) {
                    this.loadBridgeHealth();
                }
            },
            () => {
                this.networkGroupId = null;
            }
        );
    }

    private loadBridgeHealth() {
        if (!this.model?.id || !this.model?.networkId || !this.networkGroupId) return;
        this.bridgeHealthLoading = true;
        this.reporting
            .getBridgeHealth(this.model.id, this.model.networkId, this.networkGroupId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (snap) => {
                    this.bridgeHealth = snap;
                    this.lastSeenLabel = this.formatLastSeen(snap.lastSeenAt);
                    this.bridgeHealthLoading = false;
                },
                error: () => {
                    this.bridgeHealthLoading = false;
                },
            });
    }

    private loadMetrics() {
        if (!this.model?.id || !this.model?.networkId || !this.networkGroupId) return;
        this.metricsLoading = true;
        this.reporting
            .getMetrics(this.model.id, this.model.networkId, this.networkGroupId, this.metricsWindow)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.metrics = data;
                    this.rxTxSamples = data.series.rxTx.map((p) => ({ timestamp: p.t, rx: p.rx, tx: p.tx }));
                    this.systemSamples = data.series.system.map((p) => ({
                        timestamp: p.t,
                        rx: p.cpuPct,
                        tx: p.memPct,
                    }));
                    // Both rx and tx pass null straight through so the chart
                    // draws gaps (via d3 `.defined()`) for buckets where no SA
                    // has moved a packet — falling back to 0 here would mask
                    // outages as "phantom fresh".
                    this.livenessSamples = data.series.lastInboundSeconds.map((p, i) => ({
                        timestamp: p.t,
                        rx: p.v,
                        tx: data.series.lastOutboundSeconds[i]?.v ?? null,
                    }));
                    this.rekeyMarkers = (data.rekeyEvents ?? []).map((e) => ({
                        t: e.t,
                        label:
                            e.childIds.length === 1
                                ? `CHILD_SA #${e.childIds[0]} rekeyed`
                                : `CHILD_SA rekey: #${e.childIds.join(', #')}`,
                    }));
                    this.metricsLoading = false;
                },
                error: (err) => {
                    this.metricsLoading = false;
                    this.growlerService.show(
                        new GrowlerData('error', 'Metrics fetch failed', err?.error?.errors?.[0] || '')
                    );
                },
            });
    }

    private loadEvents() {
        if (!this.model?.id || !this.model?.networkId || !this.networkGroupId) return;
        const range = this.resolveEventsRange();
        if (!range) return;
        this.eventsLoading = true;
        this.reporting
            .getEvents(
                this.model.id,
                this.model.networkId,
                this.networkGroupId,
                range.gteMs,
                range.lteMs,
                this.eventKindFilter,
                this.eventSearch
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.events = data;
                    this.eventsPageIndex = 0;
                    this.eventsLoading = false;
                },
                error: () => {
                    this.eventsLoading = false;
                },
            });
    }

    applyEventSearch() {
        const term = this.eventSearchInput.trim();
        this.eventSearch = term.length > 0 ? term : null;
        this.loadEvents();
    }

    clearEventSearch() {
        this.eventSearchInput = '';
        this.eventSearch = null;
        this.loadEvents();
    }

    /** Explicit refresh. Ignored while a fetch is in flight so double-clicks do not stack. */
    refreshEvents() {
        if (!this.eventsLoading) this.loadEvents();
    }

    refreshMetrics() {
        if (!this.metricsLoading) this.loadMetrics();
    }

    setEventsRefresh(seconds: number) {
        this.eventsRefreshSeconds = seconds;
        this.eventsRefresh?.unsubscribe();
        this.eventsRefresh = this.autoRefresh(
            seconds,
            () => this.eventsLoading,
            () => this.loadEvents()
        );
    }

    setMetricsRefresh(seconds: number) {
        this.metricsRefreshSeconds = seconds;
        this.metricsRefresh?.unsubscribe();
        this.metricsRefresh = this.autoRefresh(
            seconds,
            () => this.metricsLoading,
            () => this.loadMetrics()
        );
    }

    /**
     * An interval that skips a backgrounded tab and an outstanding fetch — the first polls what
     * nobody is looking at, the second queues work behind a slow response. takeUntil(destroy$)
     * covers teardown; the returned subscription lets a panel cancel its own timer.
     */
    private autoRefresh(seconds: number, isBusy: () => boolean, load: () => void): Subscription | undefined {
        if (seconds <= 0) return undefined;
        return interval(seconds * 1000)
            .pipe(
                filter(() => typeof document === 'undefined' || !document.hidden),
                filter(() => !isBusy()),
                takeUntil(this.destroy$)
            )
            .subscribe(() => load());
    }

    private resolveEventsRange(): { gteMs: number; lteMs: number } | null {
        const now = Date.now();
        return { gteMs: now - QUICK_WINDOW_SPAN_MS[this.eventsWindow], lteMs: now };
    }

    private startBridgeHealthPolling() {
        interval(BRIDGE_HEALTH_POLL_MS)
            .pipe(
                filter(() => typeof document === 'undefined' || !document.hidden),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.loadBridgeHealth());

        if (typeof document !== 'undefined') {
            fromEvent(document, 'visibilitychange')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    if (!document.hidden) this.loadBridgeHealth();
                });
        }
    }

    private formatLastSeen(t: number | null): string {
        if (t == null) return '';
        const deltaSec = Math.max(0, Math.round((Date.now() - t) / 1000));
        if (deltaSec < 60) return `${deltaSec}s ago`;
        const m = Math.floor(deltaSec / 60);
        const s = deltaSec % 60;
        if (m < 60) return `${m}m ${s}s ago`;
        const h = Math.floor(m / 60);
        return `${h}h ${m % 60}m ago`;
    }
}
